import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getConfig } from '../config/signalr-config';

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.messageHandlers = new Map();
    this.config = getConfig();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = this.config.errorHandling.maxRetries;
  }

  // SignalR-Verbindung aufbauen
  async connect(hubUrl, accessToken = null) {
    try {
      console.log('SignalR: Attempting to connect to:', hubUrl);
      console.log('SignalR: Access token available:', !!accessToken);
      
      // Vorherige Verbindung trennen falls vorhanden
      if (this.connection) {
        await this.disconnect();
      }

      // Log Level mapping
      const logLevelMap = {
        'Critical': LogLevel.Critical,
        'Error': LogLevel.Error,
        'Warning': LogLevel.Warning,
        'Information': LogLevel.Information,
        'Debug': LogLevel.Debug,
        'Trace': LogLevel.Trace,
        'None': LogLevel.None
      };

      // Verbindung erstellen
      const connectionOptions = {
        skipNegotiation: true, // Negotiation wieder aktivieren für bessere Kompatibilität
        transport: 1 // Alle Transports zulassen (WebSockets, LongPolling, etc.)
      };

      // Token-Authentifizierung hinzufügen wenn verfügbar
      if (accessToken) {
        connectionOptions.accessTokenFactory = () => {
          console.log('SignalR: Providing access token for authentication');
          return accessToken;
        };
      }

      console.log('SignalR: Connection options:', connectionOptions);

      const connectionBuilder = new HubConnectionBuilder()
        .withUrl(hubUrl, connectionOptions)
        .withAutomaticReconnect(this.config.connectionOptions.reconnectIntervals)
        .configureLogging(logLevelMap[this.config.connectionOptions.logLevel] || LogLevel.Information);

      this.connection = connectionBuilder.build();

      // Timeout-Konfiguration
      this.connection.serverTimeoutInMilliseconds = this.config.connectionOptions.serverTimeoutInMilliseconds;
      this.connection.keepAliveIntervalInMilliseconds = this.config.connectionOptions.keepAliveIntervalInMilliseconds;

      // Event-Handler für Verbindungsstatus
      this.connection.onreconnecting((error) => {
        console.log('SignalR: Reconnecting...', error);
        this.isConnected = false;
        this.reconnectAttempts++;
      });

      this.connection.onreconnected((connectionId) => {
        console.log('SignalR: Reconnected with connectionId:', connectionId);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Event für erfolgreiche Wiederverbindung auslösen
        this.triggerEvent('reconnected', { connectionId });
      });

      this.connection.onclose((error) => {
        console.log('SignalR: Connection closed:', error);
        this.isConnected = false;
        
        // Event für Verbindungsabbruch auslösen
        this.triggerEvent('disconnected', { error });
      });

      // Verbindung starten
      console.log('SignalR: Starting connection...');
      await this.connection.start();
      this.isConnected = true;
      console.log('SignalR: Connected successfully to:', hubUrl);
      console.log('SignalR: Connection ID:', this.connection.connectionId);
      console.log('SignalR: Connection State:', this.connection.state);

      // Standard-Handler für ReceiveMessage registrieren
      this.setupDefaultHandlers();

      // Event für erfolgreiche Verbindung auslösen
      this.triggerEvent('connected', { 
        connectionId: this.connection.connectionId,
        hubUrl 
      });

      return this.connection;
    } catch (error) {
      console.error('SignalR: Error connecting:', error);
      console.error('SignalR: Error details:', {
        message: error.message,
        stack: error.stack,
        hubUrl: hubUrl,
        hasToken: !!accessToken
      });
      this.isConnected = false;
      
      if (this.config.errorHandling.showUserErrors) {
        this.triggerEvent('connectionError', { error: error.message });
      }
      
      throw error;
    }
  }

  // Standard-Handler einrichten
  setupDefaultHandlers() {
    // ReceiveMessage Handler
    this.on(this.config.events.receiveMessage, (user, message, timestamp) => {
      console.log('SignalR: ReceiveMessage:', { user, message, timestamp });
      this.triggerEvent('messageReceived', { user, message, timestamp });
    });
  }

  // Event für externe Listener auslösen
  triggerEvent(eventName, data) {
    if (this.eventListeners && this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error(`Error in event listener for ${eventName}:`, error);
        }
      });
    }
  }

  // Event Listener verwalten
  eventListeners = {};

  addEventListener(eventName, listener) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(listener);
  }

  removeEventListener(eventName, listener) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName] = this.eventListeners[eventName].filter(l => l !== listener);
    }
  }

  // Verbindung trennen
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
        this.isConnected = false;
        this.messageHandlers.clear();
        console.log('SignalR: Disconnected successfully');
      } catch (error) {
        console.error('SignalR: Error disconnecting:', error);
      } finally {
        this.connection = null;
      }
    }
  }

  // Message-Handler registrieren
  on(methodName, handler) {
    if (this.connection) {
      this.connection.on(methodName, handler);
      this.messageHandlers.set(methodName, handler);
    }
  }

  // Message-Handler entfernen
  off(methodName) {
    if (this.connection && this.messageHandlers.has(methodName)) {
      this.connection.off(methodName, this.messageHandlers.get(methodName));
      this.messageHandlers.delete(methodName);
    }
  }

  // Nachricht an Server senden
  async send(methodName, ...args) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.send(methodName, ...args);
      } catch (error) {
        console.error('SignalR: Error sending message:', error);
        if (this.config.errorHandling.showUserErrors) {
          throw new Error(`Fehler beim Senden: ${error.message}`);
        }
        throw error;
      }
    } else {
      const errorMsg = 'SignalR: Cannot send message, connection not established';
      console.warn(errorMsg);
      throw new Error('Keine Verbindung zum Server');
    }
  }

  // Nachricht an Server senden und auf Antwort warten
  async invoke(methodName, ...args) {
    if (this.connection && this.isConnected) {
      try {
        return await this.connection.invoke(methodName, ...args);
      } catch (error) {
        console.error('SignalR: Error invoking method:', error);
        if (this.config.errorHandling.showUserErrors) {
          throw new Error(`Fehler beim Aufrufen von ${methodName}: ${error.message}`);
        }
        throw error;
      }
    } else {
      const errorMsg = 'SignalR: Cannot invoke method, connection not established';
      console.warn(errorMsg);
      throw new Error('Keine Verbindung zum Server');
    }
  }

  // Verbindungsstatus prüfen
  getConnectionState() {
    return this.connection ? this.connection.state : null;
  }

  // Connection-ID abrufen
  getConnectionId() {
    return this.connection ? this.connection.connectionId : null;
  }



}

// Singleton-Instanz exportieren
const signalRService = new SignalRService();
export default signalRService;
